import { useCallback, useEffect, useMemo } from "react";
import { TIMESTAMP_PLACEHOLDER, TimestampsArr } from "../../../../utilities/useTimestamps";
import { ProctoredMedia, useProctoredMediaContainer } from "../hooks";
import { ProctoringResultViewProps } from "../ProctoringResultView";
import useFetchProctoringResult from "./useFetchProctoringResult";
import useFullScreen from "./useFullScreen";
import useResultComponents from "./useResultComponents";
import useResultTabs from "./useResultTabs";
import useSessionNotes from "./useSessionNotes";
import useSummary from "./useSummary";
import useThumbnails from "./useThumbnails";
import useVideoControls from "./useVideoControls";
import useVideoEvents from "./useVideoEvents";

const roomScanTimestampsArr: TimestampsArr = [TIMESTAMP_PLACEHOLDER, { vid: 0, start: 0 }];

export default function useProctoringResultView({
	instituteId,
	assessmentInfo,
	studentList,
	selectedStudent,
	courseId,
	proctoringSessions,
	initialProctoringSessionId,
	setShowStudentResults,
	setSelectedStudent,
	selectedLiveNotes,
	liveSessions,
	exemptedStudents,
}: ProctoringResultViewProps) {
	const { state: proctoringState, session } = useFetchProctoringResult({
		instituteId,
		assessmentInfo,
		studentList,
		selectedStudent,
		courseId,
		proctoringSessions,
		initialProctoringSessionId,
		liveSessions,
		exemptedStudents,
	});

	const videoEventReturn = useVideoEvents({ courseId, selectedLiveNotes });
	const { sessionRecordingTimestamps } = videoEventReturn.events;
	const { visibleLiveNotes, proctoringOptions } = videoEventReturn.state;
	const { timeOffset } = videoEventReturn.video;

	const webcamTimestampsArr = useMemo(
		() => sessionRecordingTimestamps.webcam[0] ?? [TIMESTAMP_PLACEHOLDER],
		[sessionRecordingTimestamps.webcam],
	);

	const webcamControl = useVideoControls(
		timeOffset,
		session.isWebCamDeleted,
		videoEventReturn.video.initialWebCamDuration,
	);
	const roomScanControl = useVideoControls(0, session.isRoomScanDeleted);
	const screenShareControl = useVideoControls(timeOffset, session.isWebCamDeleted);

	const proctoredMediaContainer = useProctoredMediaContainer({
		webcam: videoEventReturn.video.webcamVidSources,
		screencapture: videoEventReturn.video.screenVidSources,
		roomScan: useMemo(() => ({}), []),
	});

	// load thumbnails for notes
	// one thumbnail every two minutes, min 24, max 60
	const initialWebcamThumbnails = Math.min(
		Math.max(24, !webcamControl.duration ? 0 : Math.round((webcamControl.duration - timeOffset) / (2 * 60 * 1000))),
		60,
	);
	const webcamThumbnail = useThumbnails({
		initialNumThumbnails: initialWebcamThumbnails,
		duration: webcamControl.duration,
		timeOffset,
	});
	const roomScanThumbnail = useThumbnails({
		initialNumThumbnails: 24,
		duration: roomScanControl.duration,
		timeOffset: 0,
	});

	const {
		fetchNotes,
		roomScan: roomScanNotes,
		session: sessionNotes,
	} = useSessionNotes({
		courseId,
		firstEventDate: videoEventReturn.events.firstEventDate,
		sessionUuid: session.sessionUuid,
		timeOffset,
	});

	const fullScreenControl = useFullScreen();
	const componentsControl = useResultComponents({
		setShowStudentResults,
		setSelectedStudent,
		studentList,
		selectedStudent,
	});

	const {
		hideSummary,
		resetSummary,
		getSummaryData,
		generateAiSummary,
		showSummary,
		getDefaultSummary,
		saveSummary,
		sessionSummary,
		summaryLoading,
		summaryShowing,
		summaryExists,
	} = useSummary();

	const tabControl = useResultTabs({
		sessionNotes: sessionNotes.notes,
		visibleLiveNotes: visibleLiveNotes,
		hasTypedSignature: videoEventReturn.events.hasTypedSignature,
		proctoringOptions: proctoringOptions,
	});

	// Find the "start quiz" event (or first "start recording" if "start quiz" doesn't exist),
	// and move the video to that event and
	// make the offset of the video move to that point to cut out the black screen.
	useEffect(() => {
		const finalTimeOffset = timeOffset === 0 ? webcamControl.getCurrentTime() : timeOffset; // originally in timestamplinks
		webcamControl.playbackDispatch({ type: "skip", time: finalTimeOffset });
	}, [timeOffset]);

	// Used to calculate the amount of shading needed for the custom slider bar
	useEffect(() => {
		const denominator = (webcamControl.duration ?? 0) - timeOffset;
		if (denominator <= 0) {
			(document?.querySelector(":root") as HTMLElement)?.style?.setProperty("--percentage", `0px`);
			return;
		}
		const colorAmount = ((webcamControl.getCurrentTime() - timeOffset) / denominator) * 100;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		(document?.querySelector(":root") as HTMLElement)?.style?.setProperty(
			"--percentage",
			`${(colorAmount / 100 - 0.5) * 18}px`,
		);
	}, [timeOffset, webcamControl]);

	// Reset state when session changes
	useEffect(() => {
		resetSummary();

		roomScanControl.resetControls();

		getSummaryData(session.sessionUuid).catch(console.error);
		videoEventReturn.action.fetchVideoData(session.sessionUuid).catch(console.error);
		fetchNotes().catch(console.error);

		webcamControl.playbackDispatch({ type: "pause" });
		webcamControl.resetControls();
		webcamControl.playbackDispatch({ type: "skip", time: 0 });

		roomScanControl.playbackDispatch({ type: "pause" });
		roomScanControl.playbackDispatch({ type: "skip", time: 0 });
		roomScanControl.resetControls();
	}, [session.sessionUuid]);

	// Load Thumbnails
	useEffect(() => {
		if ((sessionNotes.notes.length > 0 || visibleLiveNotes.length > 0) && videoEventReturn.video.webcamVidSources[0]) {
			void webcamThumbnail.loadThumbnailsByKey(videoEventReturn.video.webcamVidSources[0], webcamTimestampsArr);
		}
	}, [
		sessionNotes.notes.length,
		videoEventReturn.video.webcamVidSources,
		visibleLiveNotes.length,
		webcamTimestampsArr,
	]);

	// Load room scan thumbnails
	useEffect(() => {
		void roomScanThumbnail.loadThumbnailsByIndex(videoEventReturn.video.roomScanVidSource, roomScanTimestampsArr);
	}, [roomScanControl.duration]);

	// update session duration if actual media duration is greater than the initial duration
	useEffect(() => {
		const maxMediaDuration = (proctoredMedia: ProctoredMedia) => {
			return Object.entries(proctoredMedia)
				.map(([_, sourceSegments]) =>
					Object.values(sourceSegments)
						.filter((segment) => segment.ok)
						.reduce((acc, segment) => segment.durationMs + acc, 0),
				)
				.reduce((acc, sourceDuration) => Math.max(acc, sourceDuration), 0);
		};
		webcamControl.changeDuration(
			Math.max(
				maxMediaDuration(proctoredMediaContainer.webcam),
				maxMediaDuration(proctoredMediaContainer.screencapture),
				videoEventReturn.video.initialWebCamDuration,
			),
		);
	}, [
		proctoredMediaContainer.screencapture,
		proctoredMediaContainer.webcam,
		videoEventReturn.video.initialWebCamDuration,
		webcamControl,
	]);

	// Add keyboard controls
	useEffect(() => {
		const handleKeyboard = (event: KeyboardEvent) => {
			tabControl.mainTab === "roomScan"
				? roomScanControl.handleKeyboard({
						event,
						setFullscreen: fullScreenControl.setFullscreen,
						ignoreKeyboardEvents: componentsControl.modal.emailModalShowing,
						isErrorCase: session.isRoomScanDeleted,
					})
				: webcamControl.handleKeyboard({
						event,
						setFullscreen: fullScreenControl.setFullscreen,
						ignoreKeyboardEvents: componentsControl.modal.emailModalShowing,
						isErrorCase: session.isWebCamDeleted,
					});
		};

		document.addEventListener("keydown", handleKeyboard);
		return () => document.removeEventListener("keydown", handleKeyboard);
	}, [
		componentsControl.modal.emailModalShowing,
		fullScreenControl.setFullscreen,
		roomScanControl,
		session.isRoomScanDeleted,
		session.isWebCamDeleted,
		tabControl.mainTab,
		webcamControl,
	]);

	const handleGetDefaultSummary = useCallback(async () => {
		return await getDefaultSummary(session.sessionUuid, videoEventReturn.events.all, timeOffset);
	}, [getDefaultSummary, session.sessionUuid, videoEventReturn.events.all, timeOffset]);

	const handleSaveSummary = useCallback(
		async (summary: string) => {
			await saveSummary(session.sessionUuid, summary);
		},
		[saveSummary, session.sessionUuid],
	);

	const handleShowAiSummary = useCallback(async () => {
		showSummary();
		await generateAiSummary(session.sessionUuid, videoEventReturn.events.all, timeOffset);
	}, [generateAiSummary, session.sessionUuid, videoEventReturn.events.all, timeOffset, showSummary]);

	const handleAddSessionNote = useCallback(
		async (content: string) => {
			const firstEventDate = videoEventReturn.events.firstEventDate;
			if (!firstEventDate) {
				return;
			}
			const timestamp = new Date(firstEventDate.getTime() + webcamControl.getCurrentTime() - timeOffset);
			return await sessionNotes.addNote(content, timestamp);
		},
		[videoEventReturn.events.firstEventDate, webcamControl, timeOffset, sessionNotes],
	);

	const handleAddRoomScanNote = useCallback(
		async (content: string) => {
			const currTime = roomScanControl.getCurrentTime() / 1000;
			return await roomScanNotes.addNote(content, currTime);
		},
		[roomScanControl, roomScanNotes],
	);

	const handleUpdateVideoEvents = useCallback(async () => {
		await videoEventReturn.action.updateVideoEvents(session.sessionUuid);
	}, [videoEventReturn.action, session.sessionUuid]);

	const handleDeleteRecording = useCallback(async () => {
		const modalType = await componentsControl.modal.deleteRecording(session.sessionUuid);

		if (modalType === "recording") {
			webcamControl.setVolume(0);
			webcamControl.toggleError(true);
			screenShareControl.toggleError(true);
		} else {
			roomScanControl.setVolume(0);
			roomScanControl.toggleError(true);
		}
	}, [componentsControl.modal, roomScanControl, screenShareControl, session.sessionUuid, webcamControl]);

	return {
		isLoading: videoEventReturn.state.isLoading,
		areVideosFetched: videoEventReturn.video.areVideosFetched,
		timeOffset,
		proctoredMediaContainer,
		proctoringState: {
			...proctoringState,
			proctoringOptions,
		},
		componentsControl: {
			...componentsControl,
			modal: {
				...componentsControl.modal,
				deleteRecording: handleDeleteRecording,
			},
		},
		summaryControl: {
			showAi: handleShowAiSummary,
			summary: sessionSummary,
			loading: summaryLoading,
			showing: summaryShowing,
			exists: summaryExists,
			show: showSummary,
			hide: hideSummary,
			save: handleSaveSummary,
			getDefault: handleGetDefaultSummary,
		},
		session,
		eventsControl: {
			all: videoEventReturn.events.all,
			severeCount: videoEventReturn.events.flagCount.severe,
			majorCount: videoEventReturn.events.flagCount.major,
			minorCount: videoEventReturn.events.flagCount.minor,
			sessionRecordingTimestamps: videoEventReturn.events.sessionRecordingTimestamps,
			startDate: videoEventReturn.events.startDate,
			update: handleUpdateVideoEvents,
			firstEventDate: videoEventReturn.events.firstEventDate,
		},
		notesControl: {
			session: {
				...sessionNotes,
				addNote: handleAddSessionNote,
				thumbnail: webcamThumbnail.thumbnails,
			},
			roomScan: {
				...roomScanNotes,
				addNote: handleAddRoomScanNote,
				thumbnail: roomScanThumbnail.thumbnails,
			},
			visibleLiveNotes,
		},
		webcamControl: {
			sources: videoEventReturn.video.webcamVidSources,
			screenSources: videoEventReturn.video.screenVidSources,
			timestampsArr: webcamTimestampsArr,
			duration: webcamControl.duration,
			options: webcamControl.controlOptions,
			playback: webcamControl.playback,
			dispatch: webcamControl.playbackDispatch,
			play: webcamControl.setPlay,
			setVolume: webcamControl.setVolume,
			setSpeed: webcamControl.setSpeed,
			toggleVolume: webcamControl.toggleVolume,
			shouldShowDownload: videoEventReturn.video.shouldShowDownload,
			showVideoUnavailable: videoEventReturn.video.showVideoUnavailable,
			showAudioThumbnail: videoEventReturn.video.showAudioThumbnail,
			shouldHideCamVid: videoEventReturn.video.shouldHideCamVid,
			showMachineLearningOverlay: videoEventReturn.video.showMachineLearningOverlay,
			hasError: webcamControl.hasError,
			toggleError: webcamControl.toggleError,
		},
		roomScanControl: {
			sources: videoEventReturn.video.roomScanVidSource,
			timestampsArr: roomScanTimestampsArr,
			duration: roomScanControl.duration,
			options: roomScanControl.controlOptions,
			playback: roomScanControl.playback,
			dispatch: roomScanControl.playbackDispatch,
			play: roomScanControl.setPlay,
			setVolume: roomScanControl.setVolume,
			setSpeed: roomScanControl.setSpeed,
			toggleVolume: roomScanControl.toggleVolume,
			hasError: roomScanControl.hasError,
			toggleError: roomScanControl.toggleError,
			changeDuration: roomScanControl.changeDuration,
			hasFinishedPlaying: roomScanControl.hasFinishedPlaying,
			isOverlayShowing: roomScanControl.showOverlay,
		},
		screenShareControl: {
			hasError: screenShareControl.hasError,
			toggleError: screenShareControl.toggleError,
		},
		tabControl,
		fullScreenControl,
	};
}

export type UseProctoringResultViewReturn = ReturnType<typeof useProctoringResultView>;
